import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDatabase } from '../contexts/DatabaseContext';
import { useTenant } from '../contexts/TenantContext';
import { useTenantTrades } from '../hooks/useTenantTrades';
import { FormService } from '../services/FormService';
import HighDensityDataGrid from '../components/common/HighDensityDataGrid';
import CertificateQuickViewDrawer from '../components/modals/CertificateQuickViewDrawer';
import ImportCertificateModal from '../components/modals/ImportCertificateModal';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiFileText,
  FiPlus,
  FiGrid,
  FiList,
  FiSearch,
  FiFilter,
  FiLayers,
  FiChevronRight,
  FiUploadCloud
} = FiIcons;

export const FormLibrary = () => {
  const { db, isReady } = useDatabase();
  const { activeTenant } = useTenant();
  const navigate = useNavigate();
  const { allowedForms, allowedCategories } = useTenantTrades();

  const [activeView, setActiveView] = useState('grid'); // 'grid' (data grid table) | 'catalog' (all templates)
  const [formInstances, setFormInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);

  // Catalog search & category filter
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogCategory, setCatalogCategory] = useState('all');

  // Trade-gated: only forms whose category is in the tenant's enabled trades
  const availableForms = allowedForms;
  // Category tabs limited to the tenant's enabled trades
  const categories = allowedCategories;

  useEffect(() => {
    if (isReady) {
      loadRecords();
    }
  }, [isReady]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const records = await db.forms.orderBy('updatedAt').reverse().toArray();
      setFormInstances(records || []);
    } catch (e) {
      console.error('Error loading form instances:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleInspectRecord = (record) => {
    setSelectedCertificate(record);
    setIsDrawerOpen(true);
  };

  const handleEditRecord = (record) => {
    navigate(`/forms/${record.formId}/${record.id}`);
  };

  const handleDeleteRecord = async (id) => {
    try {
      await FormService.deleteForm(id);
      loadRecords();
    } catch (e) {
      console.error('Failed to delete form:', e);
    }
  };

  const handleBatchStatusChange = async (ids, newStatus) => {
    try {
      await Promise.all(ids.map((id) => FormService.updateFormStatus(id, newStatus)));
      loadRecords();
    } catch (e) {
      console.error('Failed batch status update:', e);
    }
  };

  const handleBatchDelete = async (ids) => {
    try {
      await Promise.all(ids.map((id) => FormService.deleteForm(id)));
      loadRecords();
    } catch (e) {
      console.error('Failed batch delete:', e);
    }
  };

  // Filter available templates catalog
  const filteredCatalogForms = availableForms.filter((form) => {
    const matchesSearch =
      form.name.toLowerCase().includes(catalogSearch.toLowerCase()) ||
      form.description.toLowerCase().includes(catalogSearch.toLowerCase());
    const matchesCategory = catalogCategory === 'all' || form.category === catalogCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 flex flex-col h-full min-h-0">
      {/* Header bar with View Switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-black tracking-tight text-slate-900 dark:text-slate-100">
              Certificate Management Workspace
            </h1>
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
              {formInstances.length} Records
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Organization: <strong className="text-slate-700 dark:text-slate-300">{activeTenant.name}</strong> • Unified compliance registry & catalog
          </p>
        </div>

        {/* View Toggle + Vision AI Import */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsImportOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-colors"
            title="Import an existing paper or PDF certificate using Vision AI"
          >
            <SafeIcon icon={FiUploadCloud} className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Import with AI</span>
            <span className="sm:hidden">AI</span>
          </button>
          <div className="flex rounded-xl border border-slate-200 dark:border-slate-800 p-1 bg-white dark:bg-slate-900 shadow-sm">
            <button
              onClick={() => setActiveView('grid')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeView === 'grid'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              }`}
            >
              <SafeIcon icon={FiList} className="w-3.5 h-3.5" />
              <span>Certificate Records ({formInstances.length})</span>
            </button>
            <button
              onClick={() => setActiveView('catalog')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeView === 'catalog'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              }`}
            >
              <SafeIcon icon={FiGrid} className="w-3.5 h-3.5" />
              <span>Template Catalog ({availableForms.length})</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main View Mode 1: High Density Data Grid */}
      {activeView === 'grid' && (
        <div className="flex-1 flex flex-col min-h-0 space-y-3">
          <HighDensityDataGrid
            records={formInstances}
            loading={loading}
            onInspectRecord={handleInspectRecord}
            onEditRecord={handleEditRecord}
            onDeleteRecord={handleDeleteRecord}
            onBatchStatusChange={handleBatchStatusChange}
            onBatchDelete={handleBatchDelete}
            onRefresh={loadRecords}
          />
        </div>
      )}

      {/* Main View Mode 2: Certificate Form Catalog */}
      {activeView === 'catalog' && (
        <div className="space-y-6">
          {/* Catalog Filter Controls */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search certificate templates..."
                className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={catalogSearch}
                onChange={(e) => setCatalogSearch(e.target.value)}
              />
            </div>
            <div className="sm:w-64">
              <select
                className="w-full py-2 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-700 dark:text-slate-200 focus:outline-none"
                value={catalogCategory}
                onChange={(e) => setCatalogCategory(e.target.value)}
              >
                <option value="all">All Trade Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Form Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCatalogForms.map((form) => (
              <div
                key={form.id}
                onClick={() => navigate(`/forms/${form.id}`)}
                className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-lg transition-all cursor-pointer flex flex-col justify-between group"
              >
                <div>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-2xl flex items-center justify-center">
                      {form.icon}
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                      {form.category}
                    </span>
                  </div>

                  <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {form.name}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                    {form.description}
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs font-semibold text-blue-600 dark:text-blue-400">
                  <span>Start New Certificate</span>
                  <SafeIcon icon={FiChevronRight} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Vision AI Certificate Import */}
      <ImportCertificateModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
      />

      {/* Quick View Slide-Over Drawer */}
      <CertificateQuickViewDrawer
        certificate={selectedCertificate}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onStatusChange={async (id, status) => {
          await FormService.updateFormStatus(id, status);
          if (selectedCertificate) {
            setSelectedCertificate({ ...selectedCertificate, status });
          }
          loadRecords();
        }}
        onDelete={async (id) => {
          await FormService.deleteForm(id);
          setIsDrawerOpen(false);
          loadRecords();
        }}
      />
    </div>
  );
};

export default FormLibrary;
